import axios from "axios";
import AppApi from "./AppApi";
import AppStore from "../stores/Store";

export interface CreateBindingOrderModel {
  amount: number;
  description?: string;
  clientId?: string;
  recurringFrequency?: string;
  recurringExpiry?: string;
  forceCreateBinding: boolean;
  subscriptionPlanId?: string;
  fromMobile: boolean;
}

export interface CreateBindingOrderResponse {
  orderId?: string;
  externalOrderId?: string;
  paymentUrl?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface CardBindingDto {
  bindingId?: string;
  maskedPan?: string;
  cardholderName?: string;
  expiryDate?: string;
  isActive: boolean;
  createdAt: string;
}

export interface GetBindingsQueryResponse {
  bindings?: CardBindingDto[];
}

export interface PayByBindingModel {
  bindingId?: string;
  amount: number;
  description?: string;
}

export interface PayByBindingResponse {
  orderId?: string;
  externalOrderId?: string;
  success: boolean;
  errorCode?: string;
  errorMessage?: string;
}

export interface SubscriptionPlanDto {
  id: string;
  name: string;
  description: string;
  price: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
  features: string[];
  state: SubscriptionState;
}

export interface MyBillingDto {
  id: string;
  userId: string;
  subscriptionPlan: SubscriptionPlanDto | undefined;
  paidUntil: string;
  price: number;
  nextPaymentDate: string;
  isActive: boolean;
  isPaid: boolean;
  state: SubscriptionState;
  subscriptionPlanId?: string;
}

export interface PlanLimitDto {
  resourceKey: string;
  title: string;
  limit: number | null;
  used: number | null;
  resetAt?: string | null;
}

export interface BillingPlanDto {
  id: string;
  name: string;
  description: string;
  features: string[];
  price: number;
  techName: string;
  isPaid: boolean;
  limits: PlanLimitDto[];
}

export enum SubscriptionState {
  /// <summary>
  /// Активная подписка с автопродлением
  /// </summary>
  Active = "active",
  
  /// <summary>
  /// Подписка отменена пользователем (доступ до PaidUntil)
  /// </summary>
  Cancelled = "cancelled",
  
  /// <summary>
  /// Подписка истекла (PaidUntil < текущая дата)
  /// </summary>
  NotActive = "notActive"
}

export default class PaymentApi {
  private paymentClient: any;
  private billingClient: any;

  constructor(private api: AppApi, private store: AppStore) {
    this.api = api;
    this.store = store;

    // Создаем отдельный клиент для API платежей с локальным адресом для тестирования
    this.paymentClient = axios.create({
      baseURL: `${process.env.REACT_APP_BASE_URL}/payments`,
    });

    // Создаем отдельный клиент для API биллинга
    this.billingClient = axios.create({
      baseURL: `${process.env.REACT_APP_BASE_URL}/users`,
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async createBindingOrder(
    model: CreateBindingOrderModel,
    shortToken: string | null = null
  ): Promise<CreateBindingOrderResponse> {
    if (!shortToken) {
      const token = await this.store.authStore.getAuthToken();
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };
      
      const response = await this.paymentClient.post(
        "/api/v1/payments/bindings/create",
        model,
        config
      );
      return response.data;
    }
    
    const response = await this.paymentClient.post(
      `/api/v1/payments/bindings/create?short_token=${encodeURIComponent(shortToken)}`,
      model
    );
    return response.data;
  }

  async handleBindingSuccess(
    orderId: string,
    bindingId: string,
    shortToken: string | null = null
  ): Promise<void> {
    const params: Record<string, string> = { orderId, bindingId };
    if (shortToken) {
      params.short_token = shortToken;
    }
    const response = await this.paymentClient.get(
      "/api/v1/payments/bindings/success",
      { params }
    );
    return response.data;
  }

  async handleBindingFail(
    orderId: string,
    errorCode?: string,
    errorMessage?: string
  ): Promise<void> {
    const response = await this.paymentClient.get(
      "/api/v1/payments/bindings/fail",
      {
        params: {
          orderId,
          errorCode,
          errorMessage,
        },
      }
    );
    return response.data;
  }

  async getBindings(clientId?: string): Promise<GetBindingsQueryResponse> {
    const token = await this.store.authStore.getAuthToken();
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: clientId ? { clientId } : undefined,
    };

    const response = await this.paymentClient.get(
      "/api/v1/payments/bindings",
      config
    );
    return response.data;
  }

  async payByBinding(model: PayByBindingModel): Promise<PayByBindingResponse> {
    const token = await this.store.authStore.getAuthToken();
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };

    const response = await this.paymentClient.post(
      "/api/v1/payments/bindings/pay",
      model,
      config
    );
    return response.data;
  }

  async getSubscriptionPlans(): Promise<SubscriptionPlanDto[]> {
    const config = {
      headers: {
        accept: "application/json",
      },
    };

    const response = await this.billingClient.get(
      "/api/v1/billing/subscription-plans",
      config
    );
    return response.data;
  }

  async  getMyBilling(shortToken: string | null = null): Promise<MyBillingDto[]> {
    if (!shortToken) {
      const token = await this.store.authStore.getAuthToken();
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          // backend expects versioning in Accept (as in curl example); also send x-api-version explicitly for compatibility
          accept: "text/plain; x-api-version=1.0",
          "x-api-version": "1.0",
        },
      };
      
      const response = await this.billingClient.get("/api/v1/billing/my", config);
      return response.data;
    } else {
      const response = await this.billingClient.get(`/api/v1/billing/my?short_token=${encodeURIComponent(shortToken)}`);
      return response.data;
    }
  }

  async getBillingLimits(shortToken: string | null = null): Promise<BillingPlanDto> {
    if (!shortToken) {
      const token = await this.store.authStore.getAuthToken();
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      };

      const response = await this.billingClient.get("/api/v1/billing/limits", config);
      return response.data;
    }

    const response = await this.billingClient.get(
      `/api/v1/billing/limits?short_token=${encodeURIComponent(shortToken)}`
    );
    return response.data;
  }

  async cancelSubscription(subscriptionPlanId: string, shortToken: string | null = null): Promise<void> {
    if (!shortToken) {
      const token = await this.store.authStore.getAuthToken();
      const config = {
        headers: {
          accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      };
      
      await this.billingClient.post("/api/v1/billing/cancel", subscriptionPlanId, config);
    } else {
      const response = await this.billingClient.post(
        `/api/v1/billing/cancel?short_token=${encodeURIComponent(shortToken)}`, subscriptionPlanId);
      return response.data;
    }
  }
}
